import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, X, Settings } from "lucide-react";
import { ServerVariable, ServerConfiguration } from "@/lib/api-config";

interface ServerConfigurationManagerProps {
  serverUrl: string;
  serverIndex: number;
  configuration: ServerConfiguration | undefined;
  onConfigurationChange: (serverIndex: number, configuration: ServerConfiguration) => void;
}

export const ServerConfigurationManager: React.FC<ServerConfigurationManagerProps> = ({
  serverUrl,
  serverIndex,
  configuration,
  onConfigurationChange
}) => {
  // Use the configuration from props as the source of truth
  const variables = configuration?.variables || [];
  
  // Check if there are any unchanged API keys
  const hasUnchangedApiKeys = variables.some(variable => variable.value === 'TYPE_APIKEY_HERE');
  
  // Auto-expand if there are unchanged API keys
  const [isExpanded, setIsExpanded] = useState(hasUnchangedApiKeys);
  
  // Update expanded state when variables change
  React.useEffect(() => {
    if (hasUnchangedApiKeys) {
      setIsExpanded(true);
    }
  }, [hasUnchangedApiKeys]);

  const addVariable = () => {
    const newVariable: ServerVariable = {
      name: '',
      value: '',
      type: 'header'
    };
    const updatedVariables = [...variables, newVariable];
    onConfigurationChange(serverIndex, {
      serverUrl,
      variables: updatedVariables
    });
  };

  const removeVariable = (index: number) => {
    const updatedVariables = variables.filter((_, i) => i !== index);
    onConfigurationChange(serverIndex, {
      serverUrl,
      variables: updatedVariables
    });
  };

  const updateVariable = (index: number, field: keyof ServerVariable, value: string) => {
    const updatedVariables = variables.map((variable, i) => 
      i === index ? { ...variable, [field]: value } : variable
    );
    onConfigurationChange(serverIndex, {
      serverUrl,
      variables: updatedVariables
    });
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'header': return 'bg-blue-100 text-blue-800';
      case 'parameter': return 'bg-green-100 text-green-800';
      case 'body': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-2">
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full justify-between"
      >
        <div className="flex items-center gap-2">
          <Settings className="h-4 w-4" />
          <span>Configure Variables</span>
          {variables.length > 0 && (
            <span className="ml-2 px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded-full">
              {variables.length}
            </span>
          )}
        </div>
        {isExpanded ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
      </Button>

      {isExpanded && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Server Variables</CardTitle>
            {hasUnchangedApiKeys && (
              <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      API Key Required
                    </h3>
                    <div className="mt-1 text-sm text-red-700">
                      <p>In Target Servers, please replace &quot;TYPE_APIKEY_HERE&quot; with your actual API key</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </CardHeader>
          <CardContent className="space-y-3">
            {variables.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No variables configured. Add headers, parameters, or body variables to enrich API calls.
              </p>
            ) : (
              variables.map((variable, index) => (
                <div key={index} className="flex gap-2 items-end">
                  <div className="flex-1">
                    <Label htmlFor={`type-${serverIndex}-${index}`} className="text-xs">
                      Type
                    </Label>
                    <Select
                      value={variable.type}
                      onValueChange={(value: 'header' | 'parameter' | 'body') => 
                        updateVariable(index, 'type', value)
                      }
                    >
                      <SelectTrigger id={`type-${serverIndex}-${index}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="header">Header</SelectItem>
                        <SelectItem value="parameter">Parameter</SelectItem>
                        <SelectItem value="body">Body Variable</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1">
                    <Label htmlFor={`name-${serverIndex}-${index}`} className="text-xs">
                      Name
                    </Label>
                    <Input
                      id={`name-${serverIndex}-${index}`}
                      placeholder="e.g., Authorization, api_key"
                      value={variable.name}
                      onChange={(e) => updateVariable(index, 'name', e.target.value)}
                    />
                  </div>
                  <div className="flex-1">
                    <Label htmlFor={`value-${serverIndex}-${index}`} className="text-xs">
                      Value
                    </Label>
                    <Input
                      id={`value-${serverIndex}-${index}`}
                      placeholder="e.g., Bearer token123, secret_value"
                      value={variable.value}
                      onChange={(e) => updateVariable(index, 'value', e.target.value)}
                      className={variable.value === 'TYPE_APIKEY_HERE' ? 'border-red-500 bg-red-50' : ''}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeVariable(index)}
                    className="h-10 w-10 text-red-500 hover:text-red-700 hover:bg-red-50"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))
            )}
            
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addVariable}
              className="w-full"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Variable
            </Button>

            {variables.length > 0 && (
              <div className="pt-2 border-t">
                <p className="text-xs text-muted-foreground mb-2">Current Variables:</p>
                <div className="flex flex-wrap gap-1">
                  {variables.map((variable, index) => (
                    <span key={index} className={`text-xs px-2 py-1 rounded-full ${getTypeColor(variable.type)}`}>
                      {variable.type}: {variable.name || 'unnamed'}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
